/***
 *
 * This file contains utility and helper functions shared by Interop modules.
 *
 */

import { RouterClient } from "../../../clients/routerClient";
import { Logger } from "../../../clients/logger";
import { InteropClient } from "../types";
import { NotificationClient, Notification } from "../../../clients/notificationClient";

export const showLinkerWindowAtMouseEffect = (): void => {
	Logger.system.debug("showLinkerWindowAtMouseEffect");
	const params = {
		windowIdentifier: {
			componentType: "linkerWindow",
		},
	};
	RouterClient.query(`Launcher.showWindowAtMouse`, params, (err, queryResponseMessage) => {});
};

export const setComponentState = (params: { field: string; value: any; windowName: string }): void => {
	const paramsPlusWI = {
		...params,
		windowIdentifier: {
			windowName: params.windowName,
		},
	};
	RouterClient.query(`WindowService-Request-setComponentState`, paramsPlusWI, () => {});
};

export const getComponentState = async (params: { field: string; windowName: string }): Promise<any> => {
	const paramsPlusWI = {
		...params,
		windowIdentifier: {
			windowName: params.windowName,
		},
	};
	const { err, response } = await RouterClient.query(`WindowService-Request-getComponentState`, paramsPlusWI);
	if (err === "Not found") {
		/**
		 * getComponentState() returns an error of "Not found" when an app isn't in a workspace. This
		 * happens when it was launched without addToWorkspace flag. See windowClient.ts.
		 *
		 * The net result is the same as if there was no initial state.
		 */
	} else if (err?.includes("unidentified window")) {
		/**
		 * getComponentState() returns an error of "unidentified window"" when a freestanding app isn't known by the window service.
		 *
		 * The net result is the same as if there was no initial state.
		 */
		Logger.warn(
			"getComponentState: getComponentState error (which is expected for freestanding apps without a window): ",
			err.toString(),
			paramsPlusWI
		);
	} else if (err) {
		Logger.error("getComponentState: getComponentState error: ", err.toString(), paramsPlusWI);
	}
	return response?.data;
};

/**
 * Returns a name that definitively and clearly identifies the app. Some apps may have random, non-descriptive
 * appIds. Other times, multiple versions of an app will be registered with the same name but unique appIds.
 *
 * If the appId and name are the same then we return that single string. Otherwise we return a compound: `Chart(343GHS)`
 */
export const getFriendlyName = (params: { title?: string; appId: string; name: string }): string => {
	const { title, name, appId } = params;
	const titleOrName = title ?? name;
	return titleOrName ? `${titleOrName}(${appId})` : appId;
};

// Convenience function to log an error if interopAssertion isn't true.
export const interopAssertion = (label: string, condition: boolean) => {
	if (!condition) {
		Logger.system.error(`${label} interopAssertion Failed`);
	}
};

// Convenience function to returns uuids for a window name
export const uuidsFromWindowName = ({
	clients,
	windowName,
}: {
	clients: Record<string, InteropClient>;
	windowName: string;
}): string[] =>
	Object.values(clients)
		.filter((client) => client.windowName === windowName)
		.map((client) => client.uuid);

// Convenience function to generate an end-user notification
export const notifyEndUser = async (messageTitle: string, messageDetails: any, devModeOnly: boolean = false) => {
	const notification = new Notification();
	notification.source = "Finsemble";
	notification.title = messageTitle;
	notification.details = `${messageDetails} ${new Date().toString().split("GMT")[0]}`;
	notification.devModeOnly = devModeOnly;
	NotificationClient.notify(notification);
};
